import axios from 'axios';
import {GenerationListItem, GenerationOutputFormat} from '../models/Generation';

/** Rows per page. Cursor paginated, so this is a page SIZE, never an offset. */
export const GENERATIONS_PAGE_SIZE = 24;

/**
 * 410 Gone: the record is still there (with its permanent thumbnail) but the
 * master output is past its retention window or already deleted. It is a
 * normal, expected outcome of asking for a download URL — an "expired" badge,
 * not an error toast about a broken system.
 */
export function isGoneError(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 410;
}

/**
 * A `<input type="date">` value (`YYYY-MM-DD`) as the UTC instant that LOCAL
 * day begins.
 *
 * `new Date('2026-08-01')` is not this: a date-only string is defined to parse
 * as UTC midnight, which is 03:00 on the morning of the 1st in Israel — the
 * bug this exists to fix. Splitting the parts and using the multi-argument
 * constructor is what makes it local.
 *
 * The API takes any value containing 'T' as a full instant (`$gte`), and a bare
 * `YYYY-MM-DD` as a UTC day, so sending the instant is what makes the picked
 * day mean the viewer's day.
 */
export function localDayStartIso(day: string): string {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
    // Not a day string (a cleared field never reaches here) — pass it through
    // and let the server apply its legacy parsing rather than invent a bound.
    if (!parts) return day;
    const [, year, month, date] = parts;
    return new Date(Number(year), Number(month) - 1, Number(date)).toISOString();
}

/**
 * The EXCLUSIVE upper bound for a picked local end-day: the instant the NEXT
 * local day begins. An instant `to` is `$lt` server-side, so this includes
 * every row on the picked day and none of the next.
 *
 * Day+1 via the Date constructor, not +24h: it rolls months and years, and on a
 * DST boundary the local day is 23 or 25 hours long, which arithmetic on the
 * instant would get wrong.
 */
export function localDayEndExclusiveIso(day: string): string {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
    if (!parts) return day;
    const [, year, month, date] = parts;
    return new Date(Number(year), Number(month) - 1, Number(date) + 1).toISOString();
}

/**
 * 403 Forbidden: the team is not enabled for creation history. Distinct from a
 * real failure — the screen says "not available" once rather than toasting an
 * error the user can do nothing about.
 */
export function isForbiddenError(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 403;
}

const EXTENSIONS: Record<GenerationOutputFormat, string> = {png: 'png', mp4: 'mp4'};

/** `<template> - <design>.<ext>`, matching what the editor's own download names
 *  the file, so a re-download of an old generation lands as the same name. */
export function generationFileName(item: Pick<GenerationListItem,
    'templateName' | 'designKey' | 'outputFormat'>): string {
    const base = [item.templateName, item.designKey].filter(Boolean).join(' - ') || 'generation';
    return `${base}.${EXTENSIONS[item.outputFormat as GenerationOutputFormat] ?? 'png'}`;
}

/**
 * Saves a presigned S3 URL to the visitor's disk.
 *
 * The bytes are fetched into a Blob first because a cross-origin `<a download>`
 * is ignored by every browser — the attribute only works same-origin, so the
 * link would open the file in a tab instead of saving it. If that fetch fails
 * (a big mp4 on a flaky phone connection), opening the URL is still better than
 * nothing, so it is the fallback rather than an error.
 */
export async function saveUrlAsFile(url: string, filename: string): Promise<void> {
    try {
        const response = await fetch(url, {cache: 'no-store'});
        if (!response.ok) throw new Error(`Download failed: ${response.status}`);
        const objectUrl = URL.createObjectURL(await response.blob());
        triggerDownload(objectUrl, filename);
        URL.revokeObjectURL(objectUrl);
    } catch {
        window.open(url, '_blank', 'noopener');
    }
}

function triggerDownload(href: string, filename: string) {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
